import React, { useEffect, useState, useRef } from "react";
import styles from "./AssetRenderer.module.css";
import { ASSET_SOLUTION_INDEX } from "@constants/appConstants";

// Module-level cache to prevent re-importing on remount
let assetModuleCache: { component: React.ComponentType<any> } | null = null;
let assetImportPromise: Promise<{
  component: React.ComponentType<any>;
}>

const loadAssetComponent = (): Promise<{
  component: React.ComponentType<any> ;
}> => {
  if (assetModuleCache) {
    return Promise.resolve(assetModuleCache);
  }
  
  if (!assetImportPromise) {
    assetImportPromise = import("assetRenderingService/InternalQuestionSolutionView")
      .then((module:any) => {
        console.log(module);
        const Comp = module.default || module.InternalQuestionSolutionView ;
        assetModuleCache = { component: Comp };
        return assetModuleCache;
      })
      .catch((error:Error) => {
        // assetImportPromise = null; // Reset on error to allow retry
        throw error;
      });
  }
  return assetImportPromise;
};

interface SolutionProps {
  questionId?: any;
  assetTypeProps?: any;
  fetchAssetFilter?: any;
  token?: string;
  title: string;
}

const SolutionRenderer = ({
  questionId,
  assetTypeProps = {},
  fetchAssetFilter = {},
  token,
  title,
}: SolutionProps) => {
  const [hasError, setHasError] = React.useState(false);
  const [isLoaded, setIsLoaded] = useState(!!assetModuleCache);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    if (assetModuleCache) {
      setIsLoaded(true);
      return;
    }

    loadAssetComponent()
      .then(() => {
        if (isMounted.current) {
          setIsLoaded(true);
        }
      })
      .catch((error) => {
        console.error("Failed to load Asset component:", error);
        if (isMounted.current) {
          setHasError(true);
        }
      });

    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleEventOnQuestion = (event: any) => console.log("onEvent", event);

  const AssetComponent = assetModuleCache?.component;

  if (!isLoaded && !hasError) return null;

  const props: any = {
    assetId: questionId,
    assetType: "QUESTION",
    themeName: "LEARN",
    colorScheme: "light",
    onError: (event: any) => {
      console.log("OnError", event);
      if (fetchAssetFilter?.with_solutions) {
        if (
          event?.type === "FETCH_ASSET_FAILED_NOT_FOUND" ||
          event?.type === "APPLICATION_ERROR" ||
          event?.type === "FETCH_ASSET_FAILED_UNAUTHENTICATED"
        ) {
          setHasError(true);
        } else {
          setHasError(false);
        }
      }
    },
    onEvent: handleEventOnQuestion,
    authenticationToken: `Bearer ${token}`,
    fetchAssetFilter: fetchAssetFilter,
    solutionIndex:ASSET_SOLUTION_INDEX,
    assetTypeProps: {
      isQuestionFinalized: assetTypeProps?.isQuestionFinalized,
      handleOnClickSubmitQuestion: (response: any) =>
        console.log("response", response),
      provideOldFormatSelectedAnswers: true,
      showElements: assetTypeProps?.showElements,
    },
  };
  return (
    <div>
      <div className={styles.container}>
        <div className={styles.heading}>{title}</div>
        <div className={styles.assetContainer}>
          {!hasError &&
            AssetComponent &&
            React.createElement(AssetComponent, {
              key: questionId,
              ...props,
            })}
        </div>
        {hasError && (
          <div className={styles.errorMessage}>
            Unable to load the question. Please try again later.
          </div>
        )}
      </div>
    </div>
  );
};

export default SolutionRenderer;
